import type { Doctor, DoctorImage, Location } from '@/types/doctor';
import { withoutEmailAddressesInLocation } from '@/lib/consultants/locationPrivacy';

export interface BookingDoctor {
  id: string;
  type: string;
  title: string;
  slug: string;
  fullName: string;
  assistantPhoneNumber: string | null;
  heading: { raw: string | null };
  description: { raw: string | null };
  image?: DoctorImage | null;
  primaryPrice: string | null;
  secondaryPrice: string | null;
  deposit?: string | null;
  apiId: string | null;
  categoryName: string | null;
  categoryId: string | null;
  duration: string | number | null;
  bookingBufferEnabled?: boolean;
  bookingBufferHours?: number;
  earliestBookingTime?: string;
  serviceAccount: boolean;
  extraService?: string | null;
  doctifySlug: string | null;
  primarySpeciality: string | undefined;
  specialities: string[] | undefined;
  typeOfDoctor?: any;
  gender?: string;
  minAge?: number;
  maxAge?: number;
  privateLocations?: Location[];
  primaryLocations?: Location[];
  [key: string]: unknown;
}

function inferType(slug: string): string {
  return slug.toLowerCase().includes('clinic') ? 'clinic' : 'doctor';
}

export function mapDoctorToBookingDoctor(doctor: Doctor): BookingDoctor {
  return {
    id: doctor.slug,
    type: inferType(doctor.slug),
    title: doctor.title ?? doctor.fullName ?? doctor.slug,
    slug: doctor.slug,
    fullName: doctor.fullName ?? doctor.slug,
    assistantPhoneNumber: doctor.assistantPhoneNumber ?? null,
    heading: { raw: doctor.heading ?? null },
    description: { raw: doctor.description ?? null },
    // Doctor avatars are stamped as full CDN URLs in the generated doctor index
    // (src/generated/doctors.json). Passing that URL keeps the booking widget
    // independent of the per-route client manifest slice — slug-only fallbacks
    // (`<slug>.webp`) 404 whenever a route slice does not carry the avatar key.
    image: doctor.image ?? null,
    primaryPrice: doctor.primaryPrice ?? doctor.primaryPrice ?? null,
    secondaryPrice: doctor.secondaryPrice ?? null,
    deposit: doctor.deposit ?? null,
    apiId: doctor.apiId ?? null,
    categoryName: doctor.categoryName ?? null,
    categoryId: doctor.categoryId ?? null,
    duration: doctor.duration ?? null,
    bookingBufferEnabled: doctor.bookingBufferEnabled,
    bookingBufferHours: doctor.bookingBufferHours,
    earliestBookingTime: doctor.earliestBookingTime,
    serviceAccount: doctor.serviceAccount ?? false,
    doctifySlug: doctor.doctifySlug ?? doctor.slug,
    primarySpeciality: doctor.primarySpeciality,
    specialities: doctor.specialities,
    gender: doctor.gender,
    minAge: doctor.minAge,
    maxAge: doctor.maxAge,
    privateLocations: doctor.privateLocations?.map(
      withoutEmailAddressesInLocation,
    ),
    primaryLocations: doctor.primaryLocations?.map(
      withoutEmailAddressesInLocation,
    ),
  };
}

export interface BookableOptions {
  logMissingFields?: boolean;
}

export function isDoctorBookableForWidget(
  mappedDoctor: BookingDoctor,
  options?: BookableOptions,
): boolean {
  const missing: string[] = [];

  if (!mappedDoctor.apiId) missing.push('apiId');
  if (!mappedDoctor.categoryId) missing.push('categoryId');
  if (!mappedDoctor.primaryPrice && !mappedDoctor.deposit)
    missing.push('primaryPrice');

  if (missing.length > 0) {
    if (options?.logMissingFields) {
      console.warn(
        `[isDoctorBookableForWidget] Doctor "${mappedDoctor.fullName}" missing required booking fields:`,
        missing,
      );
    }
    return false;
  }

  return true;
}
